// Talks to the hub's JSON auth API (`<hub>/api/auth/*`) so signing in
// never leaves this origin.
//
// The old flow redirected to hub.estreamly.co and came back with an
// `estreamly_token` cookie on `.estreamly.co`. That works in a desktop
// browser and fails badly in an installed PWA: hub.estreamly.co is
// outside the manifest scope, so the navigation leaves the standalone
// window, and on iOS the browser context it lands in doesn't share the
// home-screen app's cookie jar — the user signs in and the app is still
// signed out. The 24-hour cookie meant repeating that daily.
//
// Here the app posts credentials from its own origin and holds the
// result itself: a short-lived access JWT plus a 90-day rotating refresh
// token. No navigation, no cookie, no scope exit.
//
// Storage is localStorage. It's readable by any script running on this
// origin, which is the accepted trade for a browser-delivered app — the
// alternative (an httponly cookie) is exactly what doesn't work here.
// The blast radius is bounded by short access tokens, refresh rotation
// with replay detection on the hub, and revocation on sign-out. Rotating
// on every launch also keeps the token young, which matters on iOS where
// script-writable storage is evicted after a period of disuse.

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const STORAGE_KEY = 'bb_hub_tokens';
const CLIENT_NAME = 'tracker';

// Renew this far ahead of expiry so an in-flight request doesn't race
// the deadline.
const REFRESH_SKEW_MS = 60 * 1000;

export class HubAuthError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'HubAuthError';
    this.code = code;
  }
}

// ── Deployment config ────────────────────────────────────────────────
//
// Fetched from our own backend rather than baked in at build time: an
// installed PWA runs whatever bundle it cached, so a build-time constant
// can't be changed without waiting for every client to update.

let configPromise = null;

export function getAuthConfig() {
  if (!configPromise) {
    configPromise = fetch(`${API_BASE_URL}/api/auth/config`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }
  return configPromise;
}

// Only used by tests and by the sign-out path, which wants a clean slate.
export function resetAuthConfigCache() {
  configPromise = null;
}

async function hubUrl() {
  const config = await getAuthConfig();
  return config && config.hub_url ? config.hub_url.replace(/\/$/, '') : null;
}

// ── Token store ──────────────────────────────────────────────────────

let listeners = [];

// Called when the session ends for a reason the user didn't ask for
// (refresh rejected, token family revoked). AuthContext subscribes so it
// can drop to the login screen instead of leaving a dead UI up.
export function onSessionLost(fn) {
  listeners.push(fn);
  return () => {
    listeners = listeners.filter((l) => l !== fn);
  };
}

function notifySessionLost(reason) {
  listeners.forEach((fn) => {
    try {
      fn(reason);
    } catch (e) {
      /* a listener throwing must not break the auth path */
    }
  });
}

export function loadTokens() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.access_token || !parsed.refresh_token) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function saveTokens(payload) {
  const record = {
    access_token: payload.access_token,
    refresh_token: payload.refresh_token,
    // Absolute deadline beats storing the duration — the app can be
    // suspended for days between two reads of this value.
    expires_at: Date.now() + (payload.expires_in || 3600) * 1000,
    user: payload.user || null,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    /* private mode / quota — the session just won't survive a reload */
  }
  return record;
}

export function clearTokens() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* nothing useful to do */
  }
}

export function hasStoredSession() {
  return loadTokens() !== null;
}

export function storedUser() {
  const tokens = loadTokens();
  return tokens ? tokens.user : null;
}

// ── Hub requests ─────────────────────────────────────────────────────

async function hubPost(path, body) {
  const base = await hubUrl();
  if (!base) throw new HubAuthError('Sign-in is not configured.', 'no_hub_url');

  let response;
  try {
    response = await fetch(`${base}/api/auth${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Deliberately no `credentials` — this API is bearer-only and the
      // hub never sends Allow-Credentials, so including them would make
      // the browser reject every response.
      body: JSON.stringify({ client: CLIENT_NAME, ...body }),
    });
  } catch (e) {
    throw new HubAuthError(
      "Couldn't reach the sign-in service. Check your connection and try again.",
      'network',
    );
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 429) {
      throw new HubAuthError(
        data.message || 'Too many attempts. Please try again shortly.',
        'too_many_requests',
      );
    }
    throw new HubAuthError(
      data.message || 'Sign-in failed. Please try again.',
      data.error || `http_${response.status}`,
    );
  }
  return data;
}

// ── Sign-in ──────────────────────────────────────────────────────────

export async function requestCode(email) {
  return hubPost('/magic-code', { email });
}

export async function verifyCode(email, code) {
  return saveTokens(await hubPost('/magic-code/verify', { email, code }));
}

export async function loginWithPassword(email, password) {
  return saveTokens(await hubPost('/login', { email, password }));
}

// Asks the hub to email a password reset link. The link opens the hub's
// reset page in the browser (it's a plain form, so that's fine even
// from the installed app); the user sets a password there and comes
// back here to sign in with it. Resolves whether or not the address
// has an account — the hub deliberately answers the same either way.
export async function requestPasswordReset(email) {
  return hubPost('/forgot-password', { email });
}

// ── Renewal ──────────────────────────────────────────────────────────

// Single-flight: an app launch fires several requests at once and they
// must not each try to rotate the refresh token. The first one through
// does the work; the rest await the same promise. Without this they'd
// race, and every loser would present an already-rotated token — which
// the hub correctly reads as a replay and punishes by revoking the whole
// family, signing the user out for doing nothing wrong.
let refreshPromise = null;

export function refreshSession() {
  if (refreshPromise) return refreshPromise;

  // Bail out before creating the shared promise, not inside it. Rejecting
  // synchronously in the body would run the cleanup before the assignment
  // below, leaving a rejected promise cached here forever — every later
  // refresh would then fail without ever calling the hub.
  const tokens = loadTokens();
  if (!tokens) return Promise.reject(new HubAuthError('Not signed in.', 'no_session'));

  const run = async () => {
    try {
      return saveTokens(await hubPost('/refresh', { refresh_token: tokens.refresh_token }));
    } catch (err) {
      // A network blip shouldn't destroy a valid session — the user may
      // just be on a train. Only drop the tokens when the hub actually
      // rejected them.
      if (err instanceof HubAuthError && err.code !== 'network') {
        clearTokens();
        notifySessionLost(err.code);
      }
      throw err;
    }
  };

  refreshPromise = run().finally(() => { refreshPromise = null; });
  return refreshPromise;
}

/** A usable access token, renewed first if it's expired or about to be. */
export async function getAccessToken() {
  const tokens = loadTokens();
  if (!tokens) return null;
  if (Date.now() < tokens.expires_at - REFRESH_SKEW_MS) {
    return tokens.access_token;
  }
  try {
    return (await refreshSession()).access_token;
  } catch (e) {
    return null;
  }
}

/** Authorization header for a request, or `{}` when signed out. */
export async function authHeaders() {
  const token = await getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Sign-out ─────────────────────────────────────────────────────────

export async function logout() {
  const tokens = loadTokens();
  clearTokens();
  if (!tokens) return;
  try {
    // Best effort: the local tokens are already gone, so a failure here
    // only means the hub keeps a row it will expire on its own.
    await hubPost('/logout', { refresh_token: tokens.refresh_token });
  } catch (e) {
    /* ignore */
  }
}
